import { Injectable, inject } from '@angular/core';
import { environment } from '../../../../environments/environment';
import { ROUTE_MASK_SEGMENTS } from './redaction-policy';
import { RedactionService } from './redaction.service';

const DEFAULT_HIDDEN_EXACT_ROUTES = ['auth/login', 'auth/logout'];

type FeedbackRouteVisibility = {
  hiddenPrefixes?: readonly string[];
  hiddenExactRoutes?: readonly string[];
};

@Injectable({
  providedIn: 'root',
})
export class RouteMaskService {
  private readonly redactionService = inject(RedactionService);
  private readonly feedbackRouteVisibility = environment.feedbackRouteVisibility as FeedbackRouteVisibility | undefined;
  private readonly hiddenRouteSegments = new Set([
    ...ROUTE_MASK_SEGMENTS.map((segment) => segment.toLowerCase()),
    ...(this.feedbackRouteVisibility?.hiddenPrefixes ?? []).map((segment) => segment.toLowerCase()),
  ]);
  private readonly hiddenExactRoutes = new Set([
    ...DEFAULT_HIDDEN_EXACT_ROUTES.map((route) => route.toLowerCase()),
    ...(this.feedbackRouteVisibility?.hiddenExactRoutes ?? []).map((route) => route.toLowerCase()),
  ]);

  normalizeRoute(url: string): string {
    try {
      const parsed = new URL(url, window.location.origin);
      return this.redactionService.redactUrl(parsed.toString()).replace(window.location.origin, '');
    } catch {
      return this.redactionService.redactText(url);
    }
  }

  maskRoute(url: string): string {
    const normalized = this.normalizeRoute(url);
    const routeKey = normalized.split('?')[0].replace(/^\//, '');
    const routeSegments = routeKey.split('/').filter(Boolean).map((segment) => segment.toLowerCase());

    if (this.hiddenExactRoutes.has(routeKey.toLowerCase())) {
      return '/hidden-route';
    }

    if (
      Array.from(this.hiddenRouteSegments).some((segment) =>
        routeSegments.some(
          (routeSegment) =>
            routeSegment === segment ||
            routeSegment.startsWith(`${segment}-`) ||
            routeSegment.startsWith(`${segment}_`),
        ),
      )
    ) {
      return '/hidden-route';
    }

    return normalized;
  }

  isHiddenRoute(url: string): boolean {
    const normalized = this.normalizeRoute(url);
    const routeKey = normalized.split('?')[0].replace(/^\//, '');
    const routeSegments = routeKey.split('/').filter(Boolean).map((segment) => segment.toLowerCase());

    if (this.hiddenExactRoutes.has(routeKey.toLowerCase())) {
      return true;
    }

    return Array.from(this.hiddenRouteSegments).some((segment) =>
      routeSegments.some(
        (routeSegment) =>
          routeSegment === segment || routeSegment.startsWith(`${segment}-`) || routeSegment.startsWith(`${segment}_`),
      ),
    );
  }
}
